import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, serverTimestamp, query, where, limit, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../Firebaseconfig';
import Navbar from '../MainComponents/Navbar';
import Footer from '../MainComponents/Footer';
import truffleBurgerImg from '../../Assets/truffle_burger.png';
import { FaStar, FaPlus, FaMinus, FaShoppingBag, FaChevronLeft } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useCart } from './CartContext';
import './SingleProduct.css';

const DEFAULT_PRODUCT = {
  id: "default",
  name: "Truffle Umami Burger",
  category: "GOURMET BURGERS • SIGNATURE SERIES",
  finalPrice: 24.00,
  description: "The ultimate sensory experience. 45-day aged Wagyu beef, infused with earthy black truffle essence, layered with umami-rich caramelized onions and melted Gruyère on a hand-crafted artisan brioche.",
  images: ["truffle_burger"],
  rating: 4.9,
  reviewsCount: "2.4k reviews"
};

const SIZES = [
  { id: 'S', label: 'S', name: 'Small', priceOffset: 0.00 },
  { id: 'M', label: 'M', name: 'Medium', priceOffset: 2.50 },
  { id: 'L', label: 'L', name: 'Large', priceOffset: 5.00 }
];

const getCustomizationOptions = (category) => {
  const catLower = (category || '').toLowerCase();
  if (catLower.includes('pizza')) {
    return [
      { id: 'extra_cheese', label: 'Extra Cheese', price: 2.00 },
      { id: 'stuffed_crust', label: 'Stuffed Crust', price: 4.00 },
      { id: 'gluten_free', label: 'Gluten-Free Crust', price: 1.50 }
    ];
  } else if (catLower.includes('sushi')) {
    return [
      { id: 'extra_ginger', label: 'Extra Ginger & Wasabi', price: 0.50 },
      { id: 'spicy_mayo', label: 'Spicy Mayo Dip', price: 1.00 },
      { id: 'avocado', label: 'Avocado Roll Upgrade', price: 1.50 }
    ];
  } else if (catLower.includes('dessert') || catLower.includes('cupcake')) {
    return [
      { id: 'extra_frosting', label: 'Extra Cream Frosting', price: 1.00 },
      { id: 'sprinkles', label: 'Rainbow Sprinkles', price: 0.50 },
      { id: 'ice_cream', label: 'Vanilla Ice Cream Scoop', price: 2.00 }
    ];
  } else {
    return [
      { id: 'extra_mayo', label: 'Extra Truffle Mayo', price: 2.50 },
      { id: 'double_patty', label: 'Double Wagyu Patty', price: 8.00 },
      { id: 'gluten_free_bun', label: 'Gluten-Free Artisan Bun', price: 1.50 }
    ];
  }
};

function SingleProduct() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedToppings, setSelectedToppings] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [isOrdering, setIsOrdering] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [company, setCompany] = useState(null);

  // Authenticate user state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Fetch product from DB or fallback
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id || id === 'default') {
        setProduct(DEFAULT_PRODUCT);
        setSelectedSize(DEFAULT_PRODUCT.sizes?.[0] || SIZES[0]);
        setLoading(false);
        return;
      }
      try {
        const docRef = doc(db, 'products', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() };
          setProduct(data);
          
          if (data.sizes && data.sizes.length > 0) {
            const hasSmall = data.sizes.some(s => s.label === 'S' || s.name?.toLowerCase() === 'small');
            if (hasSmall) {
              setSelectedSize(data.sizes[0]);
            } else {
              setSelectedSize({ id: 'default_base', label: 'S', name: 'Small', priceOffset: 0 });
            }
          } else {
            setSelectedSize({ id: 'default_base', label: 'S', name: 'Small', priceOffset: 0 });
          }
        } else {
          setProduct(DEFAULT_PRODUCT);
          setSelectedSize(SIZES[0]);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        setProduct(DEFAULT_PRODUCT);
        setSelectedSize(SIZES[0]);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  // Fetch company doc once product is loaded (to check opening hours)
  useEffect(() => {
    if (!product?.postedBy) return;
    getDoc(doc(db, 'users', product.postedBy))
      .then(snap => { if (snap.exists()) setCompany(snap.data()); })
      .catch(console.error);
  }, [product?.postedBy]);

  // Fetch related products with category 'sides'
  useEffect(() => {
    const fetchRelated = async () => {
      try {
        const q = query(
          collection(db, 'products'),
          where('category', '==', 'Sides'),
          limit(3)
        );
        const snap = await getDocs(q);
        const items = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.id !== id);
        setRelatedProducts(items.slice(0, 3));
      } catch (err) {
        console.error('Failed to fetch related products:', err);
      }
    };
    fetchRelated();
  }, [id]);

  if (loading) {
    return (
      <div className="sp-loading-container">
        <Navbar />
        <div className="sp-spinner-box">
          <div className="sp-spinner" />
          <p>{t('singleProduct.loadingExperience')}</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Compute whether the restaurant is currently closed
  const isClosed = (() => {
    if (!company?.openTime || !company?.closeTime) return false;
    const now = new Date();
    const cur = now.getHours() * 60 + now.getMinutes();
    const [oH, oM] = company.openTime.split(':').map(Number);
    const [cH, cM] = company.closeTime.split(':').map(Number);
    const open = oH * 60 + oM;
    const close = cH * 60 + cM;
    if (close < open) return !(cur >= open || cur <= close); // past midnight
    return cur < open || cur > close;
  })();

  let productSizes = [];
  if (product?.sizes?.length > 0) {
    const hasSmall = product.sizes.some(s => s.label === 'S' || s.name?.toLowerCase() === 'small');
    if (hasSmall) {
      productSizes = product.sizes;
    } else {
      productSizes = [
        { id: 'default_base', label: 'S', name: 'Small', priceOffset: 0 },
        ...product.sizes
      ];
    }
  } else {
    productSizes = [{ id: 'default_base', label: 'S', name: 'Small', priceOffset: 0 }];
  }

  const customizationOptions = product?.extras?.length > 0 
    ? product.extras 
    : getCustomizationOptions(product.category);

  const handleToppingToggle = (toppingId) => {
    setSelectedToppings((prev) =>
      prev.includes(toppingId)
        ? prev.filter((id) => id !== toppingId)
        : [...prev, toppingId]
    );
  };

  const getUnitPrice = () => {
    const base = parseFloat(product.finalPrice || product.basePrice || product.price || 24.00);
    const sizeOffset = selectedSize?.priceOffset || 0;
    const toppingsSum = selectedToppings.reduce((sum, tId) => {
      const topping = customizationOptions.find((o) => o.id === tId);
      return sum + (topping ? parseFloat(topping.price || 0) : 0);
    }, 0);
    return base + sizeOffset + toppingsSum;
  };

  const unitPrice = getUnitPrice();
  const totalPrice = unitPrice * quantity;

  const getProductImage = () => {
    if (product.images && product.images.length > 0) {
      const img = product.images[0];
      if (img === 'truffle_burger' || img.includes('truffle_burger')) {
        return truffleBurgerImg;
      }
      if (img.startsWith('blob:')) {
        return null;
      }
      return img;
    }
    return null;
  };

  const handleOrder = async () => {
    setIsOrdering(true);

    const sizeName = selectedSize?.name || selectedSize?.label || 'Regular';
    const activeToppings = selectedToppings.map(tId => {
      const option = customizationOptions.find(o => o.id === tId);
      return option ? option.label : '';
    }).filter(Boolean);

    const customizations = [`Size: ${sizeName}`, ...activeToppings];

    const compData = {
      id: product.postedBy || 'company_default',
      uid: product.postedBy || 'company_default',
      name: product.posterDisplayName || product.posterEmail?.split('@')[0] || 'Gourmet Kitchen',
      businessName: product.posterDisplayName || product.posterEmail?.split('@')[0] || 'Gourmet Kitchen',
      businessEmail: product.posterEmail || 'kitchen@crivedash.com',
      email: product.posterEmail || 'kitchen@crivedash.com',
    };

    // Make sure we pass the correct object structure to addToCart
    const productToAdd = {
      ...product,
      finalPrice: totalPrice // Pass the computed price based on size and toppings
    };

    const added = addToCart(productToAdd, compData, quantity, customizations);
    
    setIsOrdering(false);
    
    if (added) {
      navigate('/checkout');
    }
  };

  return (
    <div className="single-product-page">
      <Navbar />

      <div className="sp-back-container">
        <Link to="/products" className="sp-back-link">
          <FaChevronLeft /> {t('singleProduct.backToMenu')}
        </Link>
      </div>

      <div className={`sp-container ${!getProductImage() ? 'no-image' : ''}`}>
        {/* Left Column: Premium Image & Rating */}
        {getProductImage() && (
          <div className="sp-image-column">
            <div className="sp-image-wrapper">
              <img src={getProductImage()} alt={product.name || product.title} className="sp-main-image" />
              <div className="sp-rating-badge">
                <FaStar className="sp-star" />
                <span className="sp-rating-val">{product.rating || 4.9}</span>
                <span className="sp-reviews-count">({product.reviewsCount || `2.4k ${t('singleProduct.reviews')}`})</span>
              </div>
            </div>
          </div>
        )}

        {/* Right Column: Details & Customizations */}
        <div className="sp-details-column">
          <p className="sp-category">{product.category || "GOURMET BURGERS • SIGNATURE SERIES"}</p>
          <h1 className="sp-title">{product.name || product.title}</h1>
          
          <div className="sp-price-row">
            <span className="sp-price">${unitPrice.toFixed(2)}</span>
            <span className="sp-delivery-badge">{t('singleProduct.freeDelivery')}</span>
          </div>

          <p className="sp-description">{product.description}</p>

          <hr className="sp-divider" />

          {/* Size Selector */}
          {productSizes.length > 1 && (
            <div className="sp-size-section">
              <h3 className="sp-section-title">{t('singleProduct.selectSize')}</h3>
              <div className="sp-size-row">
                {productSizes.map((size) => (
                  <button
                    key={size.id}
                    className={`sp-size-btn ${selectedSize?.id === size.id ? 'active' : ''}`}
                    onClick={() => setSelectedSize(size)}
                    type="button"
                  >
                    <span className="sp-size-letter">{size.label}</span>
                    <span className="sp-size-label">
                      {size.name}
                    </span>
                    {size.priceOffset > 0 && (
                      <span className="sp-size-offset">+${parseFloat(size.priceOffset).toFixed(2)}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Customization Options */}
          {customizationOptions.length > 0 && (
            <div className="sp-customization-section">
              <h3 className="sp-section-title">{t('singleProduct.customizeBite')}</h3>
              <div className="sp-customization-list">
                {customizationOptions.map((option) => {
                  const isSelected = selectedToppings.includes(option.id);
                  const price = parseFloat(option.price || 0);
                  return (
                    <div
                      key={option.id}
                      className={`sp-custom-item ${isSelected ? 'active' : ''}`}
                      onClick={() => handleToppingToggle(option.id)}
                    >
                      <div className="sp-custom-left">
                        <div className={`sp-checkbox ${isSelected ? 'checked' : ''}`}>
                          {isSelected && <span className="sp-checkmark">✓</span>}
                        </div>
                        <span className="sp-custom-label">{option.label}</span>
                      </div>
                      <span className="sp-custom-price">
                        {price > 0 ? `+$${price.toFixed(2)}` : 'Free'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bottom Actions Bar */}
          <div className="sp-actions-bar">
            <div className="sp-quantity-selector">
              <button
                className="sp-quantity-btn"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                type="button"
              >
                <FaMinus />
              </button>
              <span className="sp-quantity-value">{quantity}</span>
              <button
                className="sp-quantity-btn"
                onClick={() => setQuantity((q) => q + 1)}
                type="button"
              >
                <FaPlus />
              </button>
            </div>

            {!currentUser ? (
              <Link to="/login" className="sp-add-btn sp-login-btn">
                {t('singleProduct.signInToOrder')}
              </Link>
            ) : isClosed ? (
              <button className="sp-add-btn sp-closed-btn" disabled type="button">
                🔒 Restaurant Closed · Opens {company?.openTime || '?'}
              </button>
            ) : orderSuccess ? (
              <button className="sp-add-btn success" disabled type="button">
                {t('singleProduct.ordered')}
              </button>
            ) : (
              <button
                className="sp-add-btn"
                onClick={handleOrder}
                disabled={isOrdering}
                type="button"
              >
                <FaShoppingBag className="sp-bag-icon" />
                {isOrdering ? t('singleProduct.ordering') : `${t('singleProduct.orderNow')}${totalPrice.toFixed(2)}`}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Sides / You Might Also Like ── */}
      {relatedProducts.length > 0 && (
        <div className="sp-related-section">
          <h2 className="sp-related-title">{t('singleProduct.pairsWellWith')}</h2>
          <div className="sp-related-grid">
            {relatedProducts.map(p => {
              const img = p.images && p.images.length > 0 && !p.images[0].startsWith('blob:')
                ? p.images[0]
                : (p.images && p.images[0] === 'truffle_burger' ? truffleBurgerImg : null);
              return (
                <div
                  key={p.id}
                  className="sp-related-card"
                  onClick={() => navigate(`/product/${p.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && navigate(`/product/${p.id}`)}
                >
                  {img && (
                    <div className="sp-related-img-wrap">
                      <img src={img} alt={p.name || p.title} className="sp-related-img" />
                    </div>
                  )}
                  <div className="sp-related-info">
                    <p className="sp-related-name">{p.name || p.title}</p>
                    <p className="sp-related-price">${parseFloat(p.finalPrice || p.price || 0).toFixed(2)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default SingleProduct;